import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import AttendanceService from '../../services/attendance.service';
import CycleService from '../../services/cycle.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon } from '@mui/icons-material';

export const ATTENDANCE_ABSENT = 0;
export const ATTENDANCE_PRESENT = 1;
export const ATTENDANCE_LATE = 2;

const todayInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const studentInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const AttendanceToggle = ({ value, onChange, disabled, t }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    {[
      { key: ATTENDANCE_ABSENT, label: t('attendance.absent'), color: 'error' },
      { key: ATTENDANCE_PRESENT, label: t('attendance.present'), color: 'success' },
      { key: ATTENDANCE_LATE, label: t('attendance.late'), color: 'warning' },
    ].map(({ key, label, color }) => {
      const selected = value === key;
      return (
        <Button
          key={key}
          size="small"
          variant={selected ? 'contained' : 'outlined'}
          color={color}
          disabled={disabled}
          onClick={() => onChange(key)}
          sx={{
            minWidth: 88,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            ...(selected
              ? {}
              : {
                  bgcolor: 'background.paper',
                  borderWidth: 1.5,
                }),
          }}
        >
          {label}
        </Button>
      );
    })}
  </Box>
);

const Attendance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, language } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  const [filterBy, setFilterBy] = useState('grade');
  const [date, setDate] = useState(todayInputValue());
  const [gradeId, setGradeId] = useState('');
  const [cursoId, setCursoId] = useState('');
  const [cycleId, setCycleId] = useState('');

  const [grades, setGrades] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [cycles, setCycles] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canSave = hasPermission('attendances.edit');
  const canManageTeachers = hasPermission('attendances.manage');

  const loadFilters = useCallback(async () => {
    try {
      const [cyclesRes, scopesRes] = await Promise.all([
        CycleService.getAll(),
        AttendanceService.getScopes({ cycle_id: cycleId || undefined }),
      ]);
      const cycleList = cyclesRes.data?.data || [];
      const scopeData = scopesRes.data?.data || {};
      const gradeList = scopeData.grades || [];
      const cursoList = scopeData.cursos || [];

      setCycles(cycleList);
      setGrades(gradeList);
      setCursos(cursoList);

      const resolvedCycleId = cycleId || (cycleList.length ? String(cycleList[cycleList.length - 1].id) : '');
      if (!cycleId && resolvedCycleId) setCycleId(resolvedCycleId);

      setGradeId((prev) => {
        if (prev && gradeList.some((g) => String(g.id) === prev)) return prev;
        return gradeList[0] ? String(gradeList[0].id) : '';
      });
      setCursoId((prev) => {
        if (prev && cursoList.some((c) => String(c.id) === prev)) return prev;
        return cursoList[0] ? String(cursoList[0].id) : '';
      });

      if (gradeList.length === 0 && cursoList.length > 0) {
        setFilterBy('curso');
      } else if (cursoList.length === 0 && gradeList.length > 0) {
        setFilterBy('grade');
      }
    } catch (err) {
      console.error(err);
      setError(t('attendance.errorLoadingFilters'));
    }
  }, [cycleId, t]);

  const loadSheet = useCallback(async () => {
    const filterReady =
      filterBy === 'grade' ? Boolean(gradeId) : Boolean(cursoId);
    if (!filterReady || !date) return;

    try {
      setLoading(true);
      setError('');
      const params = {
        filter_by: filterBy,
        date,
        cycle_id: cycleId || undefined,
      };
      if (filterBy === 'grade') {
        params.grade_id = gradeId;
      } else {
        params.curso_id = cursoId;
      }

      const response = await AttendanceService.getSheet(params);
      const payload = response.data?.data || {};
      setRows(payload.rows || []);
    } catch (err) {
      console.error(err);
      setRows([]);
      setError(err.response?.data?.message || t('attendance.errorLoadingSheet'));
    } finally {
      setLoading(false);
    }
  }, [cursoId, cycleId, date, filterBy, gradeId, t]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (!cycleId) return;
    AttendanceService.getScopes({ cycle_id: cycleId })
      .then((res) => {
        const scopeData = res.data?.data || {};
        setGrades(scopeData.grades || []);
        setCursos(scopeData.cursos || []);
      })
      .catch(() => {});
  }, [cycleId]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const liveSummary = useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;
    let unset = 0;
    rows.forEach((row) => {
      if (row.attendance === ATTENDANCE_PRESENT) present += 1;
      else if (row.attendance === ATTENDANCE_ABSENT) absent += 1;
      else if (row.attendance === ATTENDANCE_LATE) late += 1;
      else unset += 1;
    });
    return { present, absent, late, unset, total: rows.length };
  }, [rows]);

  const handleAttendanceChange = (asignmentId, attendance) => {
    setRows((prev) =>
      prev.map((row) =>
        row.asignment_id === asignmentId ? { ...row, attendance } : row
      )
    );
  };

  const handleSave = async () => {
    const records = rows
      .filter((row) => row.attendance !== null && row.attendance !== undefined)
      .map((row) => ({
        asignment_id: row.asignment_id,
        attendance: row.attendance,
      }));

    if (records.length === 0) {
      setSnackbar({ open: true, message: t('attendance.nothingToSave'), severity: 'warning' });
      return;
    }

    try {
      setSaving(true);
      await AttendanceService.bulkSave({
        date,
        filter_by: filterBy,
        grade_id: filterBy === 'grade' ? Number(gradeId) : null,
        curso_id: filterBy === 'curso' ? Number(cursoId) : null,
        cycle_id: cycleId ? Number(cycleId) : undefined,
        records,
      });
      setSnackbar({ open: true, message: t('attendance.saveSuccess'), severity: 'success' });
      await loadSheet();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('attendance.saveError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const formattedDateLabel = useMemo(() => {
    if (!date) return '';
    const parsed = new Date(`${date}T12:00:00`);
    return parsed.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
  }, [date, locale]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              {t('attendance.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('attendance.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canManageTeachers && (
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/attendance/assignments')}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, borderRadius: 2 }}
              >
                {t('teacherAssignments.title')}
              </Button>
            )}
            {canSave && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || loading || rows.length === 0}
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  borderRadius: 2,
                  px: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                }}
              >
                {saving ? t('attendance.saving') : t('attendance.save')}
              </Button>
            )}
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3, mb: 3, boxShadow: theme.shadows[1] }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', lg: 'center' },
                flexWrap: 'wrap',
              }}
            >
              <ToggleButtonGroup
                exclusive
                value={filterBy}
                onChange={(_, value) => value && setFilterBy(value)}
                size="small"
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  p: 0.5,
                  '& .MuiToggleButton-root': {
                    border: 0,
                    borderRadius: '8px !important',
                    px: 2.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  },
                  '& .Mui-selected': {
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                  },
                }}
              >
                {grades.length > 0 && (
                  <ToggleButton value="grade">{t('attendance.byGrade')}</ToggleButton>
                )}
                {cursos.length > 0 && (
                  <ToggleButton value="curso">{t('attendance.byCourse')}</ToggleButton>
                )}
              </ToggleButtonGroup>

              {filterBy === 'grade' ? (
                <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, lg: '0 1 220px' } }}>
                  <InputLabel>{t('attendance.grade')}</InputLabel>
                  <Select
                    label={t('attendance.grade')}
                    value={gradeId}
                    onChange={(e) => setGradeId(e.target.value)}
                  >
                    {grades.map((grade) => (
                      <MenuItem key={grade.id} value={String(grade.id)}>
                        {grade.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, lg: '0 1 220px' } }}>
                  <InputLabel>{t('attendance.course')}</InputLabel>
                  <Select
                    label={t('attendance.course')}
                    value={cursoId}
                    onChange={(e) => setCursoId(e.target.value)}
                  >
                    {cursos.map((curso) => (
                      <MenuItem key={curso.id} value={String(curso.id)}>
                        {curso.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={{ minWidth: 160, flex: { xs: 1, lg: '0 1 180px' } }}>
                <InputLabel>{t('attendance.cycle')}</InputLabel>
                <Select
                  label={t('attendance.cycle')}
                  value={cycleId}
                  onChange={(e) => setCycleId(e.target.value)}
                >
                  {cycles.map((cycle) => (
                    <MenuItem key={cycle.id} value={String(cycle.id)}>
                      {cycle.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="date"
                label={t('attendance.date')}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: locale }}
                sx={{ minWidth: 180, flex: { xs: 1, lg: '0 1 200px' } }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2.5 }}>
              <Chip
                label={t('attendance.summaryPresent', { count: liveSummary.present })}
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, bgcolor: 'success.50' }}
              />
              <Chip
                label={t('attendance.summaryAbsent', { count: liveSummary.absent })}
                color="error"
                variant="outlined"
                sx={{ fontWeight: 600, bgcolor: 'error.50' }}
              />
              <Chip
                label={t('attendance.summaryLate', { count: liveSummary.late })}
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600, bgcolor: 'warning.50' }}
              />
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">
              {grades.length === 0 && cursos.length === 0
                ? t('attendance.noAssignments')
                : t('attendance.empty')}
            </Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((row) => (
              <Card key={row.asignment_id} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      {studentInitials(row.student_name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {row.student_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {row.student_code || '—'}
                      </Typography>
                      <Chip
                        size="small"
                        label={row.grade_name}
                        color="success"
                        variant="outlined"
                        sx={{ mt: 1, height: 22, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>
                  <AttendanceToggle
                    value={row.attendance}
                    disabled={!canSave}
                    onChange={(value) => handleAttendanceChange(row.asignment_id, value)}
                    t={t}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: theme.shadows[1] }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell width={56}>#</TableCell>
                  <TableCell>{t('attendance.student')}</TableCell>
                  <TableCell>{t('attendance.code')}</TableCell>
                  <TableCell>{t('attendance.grade')}</TableCell>
                  <TableCell>{t('attendance.attendanceColumn')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.asignment_id} hover>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.85rem' }}>
                          {studentInitials(row.student_name)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.student_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.student_code || '—'}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.grade_name}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <AttendanceToggle
                        value={row.attendance}
                        disabled={!canSave}
                        onChange={(value) => handleAttendanceChange(row.asignment_id, value)}
                        t={t}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && rows.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('attendance.dateLabel', { date: formattedDateLabel })}
          </Typography>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Attendance;
